import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConstantPropertyDataService } from '../../../data/constant-property-data.service';
import { ConstantProperty } from '../../../_models/constant-property';
import { LockdownRule, ComponentAttributes, EventAttributes, AuditAttributes, LockPolicyRuleValidation } from '../../../_models/lock-policy';
import { getBaseFilter } from '../../../services/api/filter_utils';
import { ComponentType } from '../../../_models/component-type';
import { EventType } from '../../../_models/event-type';
import { ComponentTypeDataService } from '../../../data/component-type-data.service';
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {SharedModule} from "../../../shared/modules/shared-module.module";
import {
    SelectSearchComponentComponent
} from "../../../components/select-search-api/single/select-search-component.component";
import {DateTimePickerComponent} from "../../../date-time/date-time-picker/date-time-picker.component";
import {CommonModule} from "@angular/common";

type FieldType = 'string' | 'number' | 'datetime' | 'component_type' | 'event_type';

@Component({
    selector: 'rule-builder',
    templateUrl: './rule-builder.component.html',
    styleUrls: ['./rule-builder.component.less'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        MatButtonToggleGroup,
        MatButtonToggle,
        SharedModule,
        SelectSearchComponentComponent,
        DateTimePickerComponent
    ]
})
export class RuleBuilderComponent implements OnInit, OnDestroy {
  private readonly onDestroy = new Subject<void>();
  @Input() rule: LockdownRule | null;
  @Input() contradiction: LockPolicyRuleValidation['contradiction'] | null = null;
  @Input() path: string | null = null;
  @Input() availableCPIds: string[] | null = null;
  @Output() ruleChange = new EventEmitter<LockdownRule | null>();

  selectedConstantProperty: ConstantProperty | null = null;
  selectedComponentType: ComponentType | null = null;
  selectedEventType: EventType | null = null;

  get cpSearchFilters() {
    const filters: any[] = [{
      and: [
        getBaseFilter('file', 'data_type', 'ne'),
        getBaseFilter('series', 'data_type', 'ne'),
      ]
    }];

    // Add filter for available CPs if provided
    if (this.availableCPIds && this.availableCPIds.length > 0) {
      filters.push({
        name: 'id',
        op: 'in',
        val: this.availableCPIds
      });
    }

    return filters;
  }

  readonly commonAttributes = AuditAttributes;
  readonly componentAttributes = ComponentAttributes;
  readonly eventAttributes = EventAttributes;

  ruleTypes = [
    { value: 'attribute_value', viewValue: 'Attribute Value' },
    { value: 'cp_value', viewValue: 'Constant Property Value' },
    { value: 'component_attribute_value', viewValue: 'Component Attribute Value' },
    { value: 'event_attribute_value', viewValue: 'Event Attribute Value' },
    { value: 'component_type', viewValue: 'Component Type' },
    { value: 'event_type', viewValue: 'Event Type' },
    // { value: 'compound', viewValue: 'Group (AND/OR)' },
  ];

  baseOperatorMap: Record<FieldType, string[]> = {
    string: ['==', '!=', 'contains', 'not contains'],
    number: ['==', '!=', '>', '>=', '<', '<='],
    datetime: ['==', '!=', '>', '>=', '<', '<='],
    component_type: ['==', '!='],
    event_type: ['==', '!='],
  };

  operatorDesc = {
    "==": "Equals",
    "!=": "Not Equals",
    ">": "Greater than",
    ">=": "Greater than or equals",
    "<": "Less than",
    "<=": "Less than or equals",
    "contains": "Contains",
    "not contains": "Not contains",
  };

  // Track absolute/relative mode per rule instance
  dateMode: 'absolute' | 'relative' = 'relative';

  // State for relative date input
  relativeAmount = 1;
  relativeUnit: 'day' | 'week' | 'month' = 'day';
  relativeDirection: 'ago' | 'from_now' = 'ago';

  constructor(
    private constantPropertyDataService: ConstantPropertyDataService,
    private componentTypeDataService: ComponentTypeDataService
  ) {}

  ngOnInit() {
    if (this.rule.type === 'cp_value' && this.rule.cp_id) {
      this.constantPropertyDataService.getPropertyLightById(this.rule.cp_id)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(resp => {
          this.selectedConstantProperty = resp.data;
        });
    }
    if (this.rule.type === 'component_type' && (this.rule as any).component_type_id) {
      this.componentTypeDataService.getComponentType((this.rule as any).component_type_id)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(resp => {
          this.selectedComponentType = resp.data;
        });
    }
    this.setupDateFieldDefaults();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  addRule(ruleSet: any) {
    if (ruleSet.type === 'compound') {
      ruleSet.rules.push({ type: 'attribute_value', attribute: '', operator: '==', value: '' });
      this.emitRuleChange();
    }
  }

  addRuleSet(ruleSet: any) {
    if (ruleSet.type === 'compound') {
      ruleSet.rules.push({ type: 'compound', operator: 'and', rules: [] });
      this.emitRuleChange();
    }
  }

  removeRule(ruleSet: any, index: number) {
    if (ruleSet.type === 'compound') {
      ruleSet.rules.splice(index, 1);
      this.emitRuleChange();
    }
  }

  removeGroup() {
    this.rule = null;
    this.emitRuleChange();
  }

  onRuleTypeChange(newType: string): void {
    if (!this.rule) return;
    this.rule.type = newType as any;
    const allowedOperators = this.getOperators(this.getFieldType(this.rule));
    if (!allowedOperators.includes(this.rule.operator)) {
      this.rule.operator = allowedOperators[0];
    }
    this.setupDateFieldDefaults();
    this.emitRuleChange();
  }

  emitRuleChange() {
    this.ruleChange.emit(this.rule);
  }

  getOperators(fieldType: FieldType): string[] {
    if (!(fieldType in this.baseOperatorMap)) {
      throw new Error(`Invalid field type: ${fieldType}`);
    }
    return this.baseOperatorMap[fieldType];
  }

  getFieldType(rule: LockdownRule): FieldType {
    if (rule.type === 'cp_value') {
      if (!this.selectedConstantProperty) return 'string';
      const cpDataType = this.selectedConstantProperty.attributes.data_type;
      if (cpDataType === 'float') return 'number';
      if (cpDataType === 'datetime') return 'datetime';
      return 'string';
    }

    if (rule.type === 'component_attribute_value') {
      if (['start_time', 'end_time', 'created_on', 'changed_on'].includes(rule.attribute)) return 'datetime';
      if (['name', 'description'].includes(rule.attribute)) return 'string';
    }

    if (rule.type === 'event_attribute_value') {
      if (['start', 'end', 'created_on', 'changed_on'].includes(rule.attribute)) return 'datetime';
      if (['duration'].includes(rule.attribute)) return 'number';
    }

    if (rule.type === 'attribute_value') {
      if (['created_on', 'changed_on'].includes(rule.attribute)) return 'datetime';
    }

    if (rule.type === 'component_type') {
      return 'component_type';
    }

    if (rule.type === 'event_type') {
      return 'event_type';
    }

    return 'string';
  }

  initializeRule() {
    this.rule = { type: 'compound', operator: 'and', rules: [] };
    this.emitRuleChange();
  }

  onCPSelected(cp: ConstantProperty | null): void {
    if (this.rule && this.rule.type === 'cp_value') {
      this.selectedConstantProperty = cp;
      (this.rule as any).cp_id = cp ? cp.id : null;
      this.emitRuleChange();
    }
  }

  onComponentTypeSelected(ct: ComponentType | null): void {
    if (this.rule && this.rule.type === 'component_type') {
      this.selectedComponentType = ct;
      (this.rule as any).component_type_id = ct ? ct.id : null;
      this.emitRuleChange();
    }
  }

  onEventTypeSelected(et: EventType | null): void {
    if (this.rule && this.rule.type === 'event_type') {
      this.selectedEventType = et;
      (this.rule as any).event_type_id = et ? et.id : null;
      this.emitRuleChange();
    }
  }

  // Helper: check if string matches relative date format
  isRelativeDateString(val: string): boolean {
    return /^\d+:(day|week|month):(ago|from_now)$/.test(val);
  }

  // Parse a relative date string into component state
  parseRelativeDateString(val: string) {
    const match = val.match(/^(\d+):(day|week|month):(ago|from_now)$/);
    if (match) {
      this.relativeAmount = +match[1];
      this.relativeUnit = match[2] as any;
      this.relativeDirection = match[3] as any;
    } else {
      this.relativeAmount = 1;
      this.relativeUnit = 'day';
      this.relativeDirection = 'ago';
    }
  }

  // Update rule.value when relative date fields change
  updateRelativeDateString() {
    if (this.isRuleWithValue(this.rule)) {
      this.rule.value = `${this.relativeAmount}:${this.relativeUnit}:${this.relativeDirection}`;
      this.emitRuleChange();
    }
  }

  // When switching modes, sync state
  onDateModeChange(mode: 'absolute' | 'relative') {
    this.dateMode = mode;
    if (!this.isRuleWithValue(this.rule)) return;
    if (mode === 'relative') {
      if (typeof this.rule.value === 'string' && this.isRelativeDateString(this.rule.value)) {
        this.parseRelativeDateString(this.rule.value);
      } else {
        this.relativeAmount = 1;
        this.relativeUnit = 'day';
        this.relativeDirection = 'ago';
        this.updateRelativeDateString();
      }
    }
    if (mode === 'absolute') {
      this.rule.value = '';
      this.emitRuleChange();
    }
  }

  // Type guard for rules with a value property
  isRuleWithValue(rule: LockdownRule): rule is (LockdownRule & { value: any }) {
    return rule.type !== 'compound';
  }

  /**
   * Ensures that if the current rule is a date field, the date mode and value are set to sane defaults.
   * Call this after any type or attribute change.
   */
  setupDateFieldDefaults(): void {
    if (!this.rule || !this.isRuleWithValue(this.rule)) return;
    const fieldType = this.getFieldType(this.rule);
    if (fieldType === 'datetime') {
      if (typeof this.rule.value === 'string' && this.isRelativeDateString(this.rule.value)) {
        this.dateMode = 'relative';
        this.parseRelativeDateString(this.rule.value);
      } else {
        this.dateMode = 'relative';
        this.relativeAmount = 1;
        this.relativeUnit = 'day';
        this.relativeDirection = 'ago';
        this.updateRelativeDateString();
      }
    }
  }

  /**
   * Handles attribute changes and ensures date defaults are set if needed.
   * @param newAttribute The new attribute value
   */
  onAttributeChange(newAttribute: string): void {
    if (!this.rule) return;
    (this.rule as any).attribute = newAttribute;
    const allowedOperators = this.getOperators(this.getFieldType(this.rule));
    if (!allowedOperators.includes(this.rule.operator)) {
      this.rule.operator = allowedOperators[0];
    }
    this.setupDateFieldDefaults();
    this.emitRuleChange();
  }
}
